import { useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    Home,
    FolderKanban,
    Zap,
    BarChart3,
    FileCheck,
    Shield,
    Files,
    Bell,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronsUpDown,
    Check,
} from 'lucide-react'
import { LogoText } from '@/components/layout/Logo'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/lib/auth/authStore'
import { usePermissions, PERMISSIONS } from '@/lib/auth/usePermissions'
import { useOrgLogo } from '@/hooks/useOrgLogo'
import { cn } from '@/lib/utils'
import { ProjectProvider, useProjectContext } from '@/lib/context/ProjectContext'
import type { Project } from '@/types/project.types'

// ----- Stubs for features not yet rebuilt in v1.3 -----
const useUnreadCount = () => ({ data: null as { unread_count: number } | null })
// -------------------------------------------------------

interface NavItem {
    title: string
    icon: React.ElementType
    href: string
    requiredPermissions?: string[]
    requiredRoleLevel?: number
}

const navigationItems: NavItem[] = [
    {
        title: 'Dashboard',
        icon: Home,
        href: '/dashboard',
        requiredPermissions: [PERMISSIONS.DASHBOARD_VIEW],
    },
    {
        title: 'Test Ops',
        icon: FolderKanban,
        href: '/tests/projects',
        requiredPermissions: [PERMISSIONS.PROJECTS_READ],
    },
    {
        title: 'Execution Ops',
        icon: Zap,
        href: '/executions/test-runs',
        requiredPermissions: [PERMISSIONS.EXECUTIONS_READ],
    },
    {
        title: 'Result Ops',
        icon: BarChart3,
        href: '/results/test-results',
        requiredPermissions: [PERMISSIONS.REPORTS_VIEW],
    },
    {
        title: 'Requirements Ops',
        icon: FileCheck,
        href: '/requirements-ops/req-specs',
        requiredPermissions: [PERMISSIONS.REQUIREMENTS_READ],
    },
    {
        title: 'Compliance',
        icon: Shield,
        href: '/compliance/configure-audit',
        requiredPermissions: [PERMISSIONS.PROJECTS_READ],
    },
    {
        title: 'File Ops',
        icon: Files,
        href: '/file-ops/files',
        requiredPermissions: [PERMISSIONS.TESTCASES_READ],
    },
    {
        title: 'Notifications',
        icon: Bell,
        href: '/notifications',
    },
    {
        title: 'Settings',
        icon: Settings,
        href: '/settings/profile',
    },
]

function ProjectSelector() {
    const { selectedProject, setSelectedProject, projects } = useProjectContext()

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-[220px] justify-between gap-2 text-sm font-medium"
                >
                    {selectedProject ? (
                        <>
              <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selectedProject.color || '#1976D2' }}
              />
                            <span className="truncate flex-1 text-left">{selectedProject.name}</span>
                            <span className="text-xs text-muted-foreground font-mono flex-shrink-0">
                {selectedProject.prefix}
              </span>
                        </>
                    ) : (
                        <span className="text-muted-foreground flex-1 text-left">Select project…</span>
                    )}
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[240px]">
                <DropdownMenuItem
                    onSelect={() => setSelectedProject(null)}
                    className="flex items-center gap-2"
                >
          <span className="w-4 flex-shrink-0">
            {!selectedProject && <Check className="h-4 w-4" />}
          </span>
                    <span className="text-muted-foreground italic">No project selected</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {projects.map((p: Project) => (
                    <DropdownMenuItem
                        key={p.id}
                        onSelect={() => setSelectedProject(p)}
                        className="flex items-center gap-2"
                    >
            <span className="w-4 flex-shrink-0">
              {selectedProject?.id === p.id && <Check className="h-4 w-4" />}
            </span>
                        <span
                            className="h-2 w-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: p.color || '#1976D2' }}
                        />
                        <span className="truncate flex-1">{p.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">{p.prefix}</span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

interface MainLayoutProps {
    children: React.ReactNode
}

function MainLayoutInner({ children }: MainLayoutProps) {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { user, logout } = useAuthStore()
    const { hasAnyPermission, hasRoleLevel } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { data: unreadCountData } = useUnreadCount()
    const { logoUrl } = useOrgLogo()

    const visibleNavItems = useMemo(() => {
        return navigationItems.filter((item) => {
            if (!item.requiredPermissions && !item.requiredRoleLevel) return true
            if (item.requiredRoleLevel && !hasRoleLevel(item.requiredRoleLevel)) return false
            if (item.requiredPermissions && !hasAnyPermission(item.requiredPermissions)) return false
            return true
        })
    }, [user, hasAnyPermission, hasRoleLevel])

    const getInitials = (name?: string) => {
        if (!name) return 'U'
        return name
            .split(' ')
            .map((n) => n[0])
            .filter(Boolean)
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    const handleNavigation = (href: string) => {
        navigate(href)
        setSidebarOpen(false)
    }

    const isNavActive = (item: NavItem): boolean => {
        switch (item.title) {
            case 'Settings':
                return location.pathname.startsWith('/settings')
            case 'Test Ops':
                return location.pathname.startsWith('/tests')
            case 'Execution Ops':
                return location.pathname.startsWith('/executions')
            case 'Result Ops':
                return location.pathname.startsWith('/results')
            case 'Requirements Ops':
                return location.pathname.startsWith('/requirements-ops')
            case 'File Ops':
                return location.pathname.startsWith('/file-ops')
            case 'Compliance':
                return location.pathname.startsWith('/compliance')
            default:
                return location.pathname === item.href
        }
    }

    const renderNavItems = (items: NavItem[]) =>
        items.map((item) => {
            const isActive = isNavActive(item)
            return (
                <button
                    key={item.href}
                    onClick={() => handleNavigation(item.href)}
                    className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive ? 'bg-[#1976D2] text-white' : 'text-gray-700 hover:bg-gray-100'
                    )}
                >
                    <item.icon className="h-5 w-5" />
                    {item.title}
                </button>
            )
        })

    const SidebarHeader = () => (
        <div className="flex h-16 items-center border-b px-6 gap-3">
            {logoUrl && (
                <img
                    src={logoUrl}
                    alt="Organization logo"
                    className="h-12 w-12 rounded object-contain shrink-0"
                />
            )}
            <LogoText size="md" />
        </div>
    )

    return (
        <div className="flex h-screen overflow-hidden">
            {/* Sidebar - Desktop */}
            <div className="hidden w-64 flex-col border-r bg-white md:flex">
                <SidebarHeader />

                <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                    {renderNavItems(visibleNavItems)}
                </nav>

                <div className="border-t p-4">
                    <div className="flex items-center gap-3">
                        <Avatar>
                            <AvatarFallback>{getInitials(user?.login)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 overflow-hidden">
                            <p className="truncate text-sm font-medium">{user?.login || 'User'}</p>
                            <p className="truncate text-xs text-gray-500">{user?.email || ''}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={logout} title="Logout">
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Mobile Sidebar */}
            <div
                className={cn(
                    'fixed inset-y-0 left-0 z-50 w-64 transform bg-white transition-transform duration-300 ease-in-out md:hidden',
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                <div className="flex h-16 items-center justify-between border-b px-6">
                    <div className="flex items-center gap-3">
                        {logoUrl && (
                            <img
                                src={logoUrl}
                                alt="Organization logo"
                                className="h-12 w-12 rounded object-contain shrink-0"
                            />
                        )}
                        <LogoText size="md" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                    {renderNavItems(visibleNavItems)}
                </nav>
            </div>

            {/* Main Content Area */}
            <div className="flex flex-1 flex-col overflow-hidden">
                <header className="flex h-16 items-center justify-between border-b bg-white px-6">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                        <ProjectSelector />
                    </div>

                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative"
                        onClick={() => navigate('/notifications')}
                    >
                        <Bell className="h-5 w-5" />
                        {unreadCountData && unreadCountData.unread_count > 0 && (
                            <Badge
                                variant="destructive"
                                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs"
                            >
                                {unreadCountData.unread_count > 99 ? '99+' : unreadCountData.unread_count}
                            </Badge>
                        )}
                    </Button>
                </header>

                <main className="flex-1 overflow-y-auto bg-gray-50">{children}</main>
            </div>
        </div>
    )
}

export default function MainLayout({ children }: MainLayoutProps) {
    return (
        <ProjectProvider>
            <MainLayoutInner>{children}</MainLayoutInner>
        </ProjectProvider>
    )
}
